/**
 * GeoData - Handles real geospatial data processing and biome detection
 */

export class GeoDataService {
  constructor() {
    this.ecoregionsData = null;
    this.spatialIndex = new Map();
    this.elevationData = null;
    this.climateData = null;
    this.continentBounds = this.initializeContinentBounds();
    this.dataLoaded = false;
  }

  /**
   * Initialize continent boundary data
   */
  initializeContinentBounds() {
    return {
      'North America': {
        bounds: { north: 83.2, south: 7.2, east: -12.1, west: -168.2 },
        countries: ['US', 'CA', 'MX', 'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA']
      },
      'South America': {
        bounds: { north: 12.6, south: -55.1, east: -34.4, west: -81.3 },
        countries: ['BR', 'AR', 'PE', 'CO', 'VE', 'CL', 'EC', 'BO', 'UY', 'GY', 'SR', 'GF']
      },
      'Europe': {
        bounds: { north: 81.9, south: 27.6, east: 69.1, west: -31.3 },
        countries: ['RU', 'DE', 'FR', 'GB', 'IT', 'ES', 'PL', 'NO', 'SE', 'FI']
      },
      'Asia': {
        bounds: { north: 81.2, south: -11.0, east: -169.0, west: 26.0 },
        countries: ['CN', 'IN', 'ID', 'JP', 'KZ', 'MN', 'IR', 'TH', 'MM', 'AF']
      },
      'Africa': {
        bounds: { north: 37.4, south: -34.8, east: 63.5, west: -25.4 },
        countries: ['DZ', 'CD', 'SD', 'LY', 'TD', 'NE', 'AO', 'ML', 'ZA', 'ET']
      },
      'Australia': {
        bounds: { north: -9.2, south: -55.3, east: 179.0, west: 110.0 },
        countries: ['AU', 'NZ', 'PG', 'FJ', 'NC', 'VU', 'SB', 'TO']
      },
      'Antarctica': {
        bounds: { north: -60.0, south: -90.0, east: 180.0, west: -180.0 },
        countries: ['AQ']
      }
    };
  }

  /**
   * Load real ecoregions data from WWF or similar source
   */
  async loadEcoregionsData() {
    try {
      console.log('Loading biome classification data...');

      // In production, this would load from a processed GeoJSON file
      // For now, we'll create a more sophisticated classification system
      // Try to load external data file if available, but don't show errors if missing
      let response = null;
      try {
        response = await fetch('/data/ecoregions.json');
        if (!response.ok) {
          response = null;
        }
      } catch (error) {
        // Silently handle missing data file - this is expected in demo mode
        response = null;
      }

      if (response) {
        this.ecoregionsData = await response.json();
        console.log('✅ Loaded external ecoregions data');
      } else {
        console.log('🔶 Using built-in biome classification (demo mode)');
        this.ecoregionsData = this.createEnhancedBiomeClassification();
      }

      // Build spatial index for faster lookups
      this.buildSpatialIndex();
      this.dataLoaded = true;

      console.log('✅ Biome classification system ready');
    } catch (error) {
      console.error('Failed to load ecoregions data:', error);
      // Fallback to enhanced built-in system
      this.ecoregionsData = this.createEnhancedBiomeClassification();
      this.buildSpatialIndex();
      this.dataLoaded = true;
    }
  }

  /**
   * Create enhanced biome classification rules
   */
  createEnhancedBiomeClassification() {
    return {
      version: '2.0.0',
      source: 'Enhanced Geographic Classification',
      lastUpdated: new Date().toISOString(),
      rules: [
        // Tropical regions
        {
          biome: 'Tropical Rainforest',
          conditions: [
            { type: 'latitude', min: -10, max: 10 },
            { type: 'longitude', ranges: [[-90, -60], [-10, 50], [95, 155]] },
            { type: 'elevation', max: 1500 }
          ]
        },
        {
          biome: 'Tropical Savanna',
          conditions: [
            { type: 'latitude', min: -20, max: 20 },
            { type: 'longitude', ranges: [[-25, 50], [110, 150]] },
            { type: 'not_rainforest', value: true }
          ]
        },

        // Desert regions
        {
          biome: 'Hot Desert',
          conditions: [
            { type: 'latitude', ranges: [[15, 35], [-35, -15]] },
            { type: 'longitude', ranges: [[-125, -105], [-10, 60], [110, 140]] }
          ]
        },
        {
          biome: 'Cold Desert',
          conditions: [
            { type: 'latitude', ranges: [[35, 50], [-50, -35]] },
            { type: 'longitude', ranges: [[-120, -100], [75, 110]] }
          ]
        },

        // Temperate regions
        {
          biome: 'Temperate Deciduous Forest',
          conditions: [
            { type: 'latitude', ranges: [[30, 60], [-60, -30]] },
            { type: 'longitude', ranges: [[-95, -65], [-10, 40], [125, 145]] }
          ]
        },
        {
          biome: 'Temperate Coniferous Forest',
          conditions: [
            { type: 'latitude', ranges: [[45, 65], [-65, -45]] },
            { type: 'longitude', ranges: [[-170, -50], [20, 170]] }
          ]
        },
        {
          biome: 'Temperate Grassland',
          conditions: [
            { type: 'latitude', ranges: [[25, 55], [-55, -25]] },
            { type: 'longitude', ranges: [[-105, -95], [20, 120]] }
          ]
        },

        // Mountain regions
        {
          biome: 'Mountain',
          conditions: [
            { type: 'elevation', min: 1500 },
            { type: 'mountain_ranges', check: true }
          ]
        },

        // Polar and subpolar
        {
          biome: 'Tundra',
          conditions: [
            { type: 'latitude', ranges: [[60, 75], [-75, -60]] }
          ]
        },
        {
          biome: 'Ice Sheet',
          conditions: [
            { type: 'latitude', ranges: [[75, 90], [-90, -75]] }
          ]
        },

        // Aquatic (oceans and large water bodies)
        {
          biome: 'Marine',
          conditions: [
            { type: 'water_body', value: true }
          ]
        }
      ]
    };
  }

  /**
   * Build spatial index for efficient biome lookups
   */
  buildSpatialIndex() {
    // Create a grid-based spatial index for O(1) lookups
    const gridSize = 5; // 5-degree grid cells for indexing

    for (let lat = -90; lat < 90; lat += gridSize) {
      for (let lon = -180; lon < 180; lon += gridSize) {
        const key = `${Math.floor(lat / gridSize)}_${Math.floor(lon / gridSize)}`;
        const centerLat = lat + gridSize / 2;
        const centerLon = lon + gridSize / 2;

        const biome = this.classifyBiome(centerLat, centerLon);
        this.spatialIndex.set(key, {
          biome,
          lat: centerLat,
          lon: centerLon,
          confidence: this.calculateConfidence(centerLat, centerLon, biome)
        });
      }
    }

    console.log(`Built spatial index with ${this.spatialIndex.size} entries`);
  }

  /**
   * Enhanced biome classification using multiple factors
   */
  classifyBiome(lat, lon, elevation = 0) {
    const absLat = Math.abs(lat);

    // Check for water bodies first
    if (this.isWaterBody(lat, lon)) {
      return 'Marine';
    }

    // Mountain regions (elevation-based)
    if (elevation > 1500 || this.isMountainRegion(lat, lon)) {
      if (absLat > 60) return 'Alpine Tundra';
      if (absLat > 35) return 'Mountain Forest';
      return 'Mountain';
    }

    // Ice sheets and polar regions
    if (absLat >= 75) {
      return 'Ice Sheet';
    }

    // Tundra regions
    if (absLat >= 60) {
      return 'Tundra';
    }

    // Taiga/Boreal Forest
    if (absLat >= 50 && absLat < 60) {
      if (this.isTaigaRegion(lat, lon)) {
        return 'Taiga';
      }
    }

    // Temperate regions
    if (absLat >= 30 && absLat < 50) {
      if (this.isDesertRegion(lat, lon)) {
        return 'Temperate Desert';
      }
      if (this.isGrasslandRegion(lat, lon)) {
        return 'Temperate Grassland';
      }
      if (this.isDecidiousForestRegion(lat, lon)) {
        return 'Temperate Deciduous Forest';
      }
      return 'Temperate Mixed Forest';
    }

    // Subtropical regions
    if (absLat >= 20 && absLat < 30) {
      if (this.isDesertRegion(lat, lon)) {
        return 'Hot Desert';
      }
      if (this.isMediterraneanRegion(lat, lon)) {
        return 'Mediterranean';
      }
      return 'Subtropical';
    }

    // Tropical regions
    if (absLat < 20) {
      if (this.isDesertRegion(lat, lon)) {
        return 'Hot Desert';
      }
      if (this.isSavannaRegion(lat, lon)) {
        return 'Tropical Savanna';
      }
      if (this.isWetlandRegion(lat, lon)) {
        return 'Tropical Wetland';
      }
      return 'Tropical Rainforest';
    }

    return 'Mixed';
  }

  /**
   * Check if coordinates are in a water body
   */
  isWaterBody(lat, lon) {
    // Major ocean boundaries (simplified)
    const oceanRegions = [
      // Pacific Ocean
      { latMin: -70, latMax: 70, lonMin: -180, lonMax: -70 },
      { latMin: -70, latMax: 70, lonMin: 120, lonMax: 180 },

      // Atlantic Ocean
      { latMin: -70, latMax: 70, lonMin: -70, lonMax: 20 },

      // Indian Ocean
      { latMin: -70, latMax: 30, lonMin: 20, lonMax: 120 },

      // Arctic Ocean
      { latMin: 65, latMax: 90, lonMin: -180, lonMax: 180 }
    ];

    return oceanRegions.some(ocean =>
      lat >= ocean.latMin && lat <= ocean.latMax &&
      lon >= ocean.lonMin && lon <= ocean.lonMax &&
      !this.isLandMass(lat, lon)
    );
  }

  /**
   * Simple land mass detection (major continents)
   */
  isLandMass(lat, lon) {
    // This would use detailed coastline data in production
    // For now, use continent bounding boxes
    for (const [continent, data] of Object.entries(this.continentBounds)) {
      const bounds = data.bounds;
      if (lat >= bounds.south && lat <= bounds.north &&
          lon >= bounds.west && lon <= bounds.east) {
        return true;
      }
    }
    return false;
  }

  /**
   * Enhanced desert region detection
   */
  isDesertRegion(lat, lon) {
    const desertRegions = [
      // Sahara
      { latMin: 10, latMax: 35, lonMin: -20, lonMax: 40 },
      // Arabian Peninsula
      { latMin: 12, latMax: 32, lonMin: 34, lonMax: 60 },
      // Gobi/Central Asia
      { latMin: 35, latMax: 50, lonMin: 75, lonMax: 125 },
      // Australian Outback
      { latMin: -35, latMax: -15, lonMin: 110, lonMax: 155 },
      // Southwestern US/Northern Mexico
      { latMin: 25, latMax: 40, lonMin: -125, lonMax: -100 },
      // Patagonian Desert
      { latMin: -55, latMax: -35, lonMin: -75, lonMax: -65 },
      // Kalahari
      { latMin: -30, latMax: -15, lonMin: 15, lonMax: 30 }
    ];

    return desertRegions.some(region =>
      lat >= region.latMin && lat <= region.latMax &&
      lon >= region.lonMin && lon <= region.lonMax
    );
  }

  /**
   * Grassland region detection
   */
  isGrasslandRegion(lat, lon) {
    const grasslandRegions = [
      // Great Plains (North America)
      { latMin: 30, latMax: 55, lonMin: -105, lonMax: -95 },
      // Pampas (South America)
      { latMin: -40, latMax: -25, lonMin: -65, lonMax: -55 },
      // Eurasian Steppe
      { latMin: 40, latMax: 55, lonMin: 20, lonMax: 120 },
      // African Savanna
      { latMin: -20, latMax: 15, lonMin: -20, lonMax: 50 }
    ];

    return grasslandRegions.some(region =>
      lat >= region.latMin && lat <= region.latMax &&
      lon >= region.lonMin && lon <= region.lonMax
    );
  }

  /**
   * Taiga region detection
   */
  isTaigaRegion(lat, lon) {
    return (
      (lat >= 50 && lat <= 70 && lon >= -170 && lon <= -50) || // North American Boreal
      (lat >= 50 && lat <= 70 && lon >= 20 && lon <= 170)     // Eurasian Taiga
    );
  }

  /**
   * Deciduous forest region detection
   */
  isDecidiousForestRegion(lat, lon) {
    const forestRegions = [
      // Eastern North America
      { latMin: 30, latMax: 50, lonMin: -100, lonMax: -65 },
      // Europe
      { latMin: 40, latMax: 60, lonMin: -10, lonMax: 40 },
      // East Asia
      { latMin: 25, latMax: 45, lonMin: 100, lonMax: 145 }
    ];

    return forestRegions.some(region =>
      lat >= region.latMin && lat <= region.latMax &&
      lon >= region.lonMin && lon <= region.lonMax
    );
  }

  /**
   * Savanna region detection
   */
  isSavannaRegion(lat, lon) {
    return (
      // African Savanna
      (lat >= -20 && lat <= 15 && lon >= -20 && lon <= 50 &&
       !this.isDesertRegion(lat, lon)) ||
      // Australian Savanna
      (lat >= -20 && lat <= -10 && lon >= 120 && lon <= 145) ||
      // South American Savanna (Cerrado)
      (lat >= -25 && lat <= -5 && lon >= -60 && lon <= -45)
    );
  }

  /**
   * Mediterranean region detection
   */
  isMediterraneanRegion(lat, lon) {
    const mediterraneanRegions = [
      // Mediterranean Basin
      { latMin: 30, latMax: 45, lonMin: -10, lonMax: 40 },
      // California
      { latMin: 30, latMax: 42, lonMin: -125, lonMax: -115 },
      // Chile
      { latMin: -40, latMax: -30, lonMin: -75, lonMax: -70 },
      // South Africa (Cape)
      { latMin: -35, latMax: -30, lonMin: 15, lonMax: 25 },
      // Southwest Australia
      { latMin: -35, latMax: -30, lonMin: 115, lonMax: 125 }
    ];

    return mediterraneanRegions.some(region =>
      lat >= region.latMin && lat <= region.latMax &&
      lon >= region.lonMin && lon <= region.lonMax
    );
  }

  /**
   * Wetland region detection
   */
  isWetlandRegion(lat, lon) {
    const wetlandRegions = [
      // Amazon Basin
      { latMin: -10, latMax: 5, lonMin: -75, lonMax: -45 },
      // Congo Basin
      { latMin: -5, latMax: 5, lonMin: 10, lonMax: 30 },
      // Southeast Asia
      { latMin: -10, latMax: 10, lonMin: 95, lonMax: 155 }
    ];

    return wetlandRegions.some(region =>
      lat >= region.latMin && lat <= region.latMax &&
      lon >= region.lonMin && lon <= region.lonMax
    );
  }

  /**
   * Enhanced mountain region detection
   */
  isMountainRegion(lat, lon) {
    const mountainRanges = [
      // Rocky Mountains
      { latMin: 25, latMax: 60, lonMin: -125, lonMax: -105 },
      // Appalachian Mountains
      { latMin: 25, latMax: 50, lonMin: -85, lonMax: -75 },
      // Andes
      { latMin: -55, latMax: 15, lonMin: -80, lonMax: -65 },
      // Alps
      { latMin: 40, latMax: 50, lonMin: 5, lonMax: 15 },
      // Himalayas
      { latMin: 25, latMax: 40, lonMin: 70, lonMax: 105 },
      // Ural Mountains
      { latMin: 45, latMax: 70, lonMin: 55, lonMax: 65 },
      // Caucasus
      { latMin: 40, latMax: 45, lonMin: 40, lonMax: 50 },
      // Atlas Mountains
      { latMin: 28, latMax: 38, lonMin: -10, lonMax: 5 },
      // Ethiopian Highlands
      { latMin: 5, latMax: 18, lonMin: 35, lonMax: 45 },
      // Australian Alps
      { latMin: -40, latMax: -35, lonMin: 145, lonMax: 155 }
    ];

    return mountainRanges.some(range =>
      lat >= range.latMin && lat <= range.latMax &&
      lon >= range.lonMin && lon <= range.lonMax
    );
  }

  /**
   * Calculate confidence score for biome classification
   */
  calculateConfidence(lat, lon, biome) {
    let confidence = 0.7; // Base confidence

    // Increase confidence for well-defined regions
    if (Math.abs(lat) > 60) confidence += 0.2; // Polar regions are well-defined
    if (this.isDesertRegion(lat, lon)) confidence += 0.15;
    if (this.isMountainRegion(lat, lon)) confidence += 0.1;

    // Decrease confidence for boundary regions
    const absLat = Math.abs(lat);
    if (absLat >= 25 && absLat <= 35) confidence -= 0.1; // Subtropical boundaries
    if (absLat >= 45 && absLat <= 55) confidence -= 0.05; // Temperate boundaries

    return Math.min(0.95, Math.max(0.3, confidence));
  }

  /**
   * Get continent for given coordinates
   */
  getContinent(lat, lon) {
    for (const [continent, data] of Object.entries(this.continentBounds)) {
      const bounds = data.bounds;
      if (lat >= bounds.south && lat <= bounds.north &&
          lon >= bounds.west && lon <= bounds.east) {
        return continent;
      }
    }
    return 'Ocean';
  }

  /**
   * Get biome for coordinates using spatial index
   */
  getBiomeForCoordinates(lat, lon, elevation = 0) {
    if (!this.dataLoaded) {
      console.warn('Geo data not loaded, using fallback');
      return this.classifyBiome(lat, lon, elevation);
    }

    // Use spatial index for fast lookup
    const gridSize = 5;
    const indexKey = `${Math.floor(lat / gridSize)}_${Math.floor(lon / gridSize)}`;
    const indexResult = this.spatialIndex.get(indexKey);

    if (indexResult) {
      // For better accuracy, check nearby cells if confidence is low
      if (indexResult.confidence < 0.6) {
        return this.classifyBiome(lat, lon, elevation);
      }
      return indexResult.biome;
    }

    // Fallback to direct classification
    return this.classifyBiome(lat, lon, elevation);
  }

  /**
   * Get detailed information about a location
   */
  getLocationDetails(lat, lon) {
    const biome = this.getBiomeForCoordinates(lat, lon);
    const continent = this.getContinent(lat, lon);
    const confidence = this.calculateConfidence(lat, lon, biome);

    return {
      biome,
      continent,
      confidence,
      coordinates: { lat, lon },
      climateZone: this.getClimateZone(lat),
      elevationEstimate: this.estimateElevation(lat, lon),
      nearbyFeatures: this.getNearbyFeatures(lat, lon)
    };
  }

  /**
   * Get climate zone based on latitude
   */
  getClimateZone(lat) {
    const absLat = Math.abs(lat);

    if (absLat >= 66.5) return 'Polar';
    if (absLat >= 35) return 'Temperate';
    if (absLat >= 23.5) return 'Subtropical';
    return 'Tropical';
  }

  /**
   * Estimate elevation based on location (simplified)
   */
  estimateElevation(lat, lon) {
    if (this.isMountainRegion(lat, lon)) {
      // Return estimated mountain elevation
      return Math.floor(Math.random() * 3000) + 1500;
    }

    // Coastal and lowland areas
    if (this.isNearCoast(lat, lon)) {
      return Math.floor(Math.random() * 200);
    }

    // Continental interior
    return Math.floor(Math.random() * 800) + 100;
  }

  /**
   * Simple coastal proximity check
   */
  isNearCoast(lat, lon) {
    // This would use detailed coastline data in production
    // For now, use simple heuristics
    const continent = this.getContinent(lat, lon);
    if (continent === 'Ocean') return true;

    // Check if near continent edges (simplified)
    const bounds = this.continentBounds[continent]?.bounds;
    if (!bounds) return false;

    const edgeDistance = 5; // degrees
    return (
      lat <= bounds.south + edgeDistance ||
      lat >= bounds.north - edgeDistance ||
      lon <= bounds.west + edgeDistance ||
      lon >= bounds.east - edgeDistance
    );
  }

  /**
   * Get nearby geographical features
   */
  getNearbyFeatures(lat, lon) {
    const features = [];

    if (this.isMountainRegion(lat, lon)) {
      features.push('Mountain Range');
    }

    if (this.isNearCoast(lat, lon)) {
      features.push('Coastal Area');
    }

    if (this.isWaterBody(lat, lon)) {
      features.push('Water Body');
    }

    if (Math.abs(lat) < 5) {
      features.push('Near Equator');
    }

    return features;
  }

  /**
   * Check if data service is ready
   */
  isReady() {
    return this.dataLoaded;
  }

  /**
   * Get service statistics
   */
  getStats() {
    return {
      dataLoaded: this.dataLoaded,
      indexSize: this.spatialIndex.size,
      dataVersion: this.ecoregionsData?.version || 'unknown',
      continents: Object.keys(this.continentBounds).length
    };
  }
}
